import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
    Alert,
} from "@mui/material";
import jsQR from "jsqr";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

function QRCodeScanner({
    onQRCodeScanned,
    disabled = false,
    isScanning: externalIsScanning = null,
    setIsScanning: setExternalIsScanning = null,
}) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [internalIsScanning, setInternalIsScanning] = useState(false);
    const [error, setError] = useState(null);
    const [cameraPermission, setCameraPermission] = useState(null);
    const [debugInfo, setDebugInfo] = useState("");
    const [testCount, setTestCount] = useState(0);

    // Use external state if provided, otherwise use internal state
    const isScanning =
        externalIsScanning !== null ? externalIsScanning : internalIsScanning;
    const setIsScanning = setExternalIsScanning || setInternalIsScanning;
    const scanningRef = useRef(false);
    const scanIntervalRef = useRef(null);
    const lastDetectionRef = useRef(null);
    const frameCounterRef = useRef(0);
    const noDetectionRef = useRef(0);

    // Request camera access
    const startCamera = async () => {
        try {
            setError(null);
            setCameraPermission("requesting");
            scanningRef.current = false;

            // Check if browser supports getUserMedia
            if (
                !navigator.mediaDevices ||
                !navigator.mediaDevices.getUserMedia
            ) {
                setError("Camera not supported on this device");
                setCameraPermission("denied");
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
                audio: false,
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setCameraPermission("granted");

                // Wait for video to be ready, then start scanning
                videoRef.current.onloadedmetadata = () => {
                    setDebugInfo("Video loaded, starting playback...");
                    videoRef.current.play().catch((err) => {
                        setDebugInfo(`Play error: ${err.message}`);
                        setError("Unable to play camera stream");
                        setIsScanning(false);
                    });
                };
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            let errorMsg = "Unable to access camera.";
            if (err.name === "NotAllowedError") {
                errorMsg =
                    "Camera permission denied. Please allow camera access in settings.";
            } else if (err.name === "NotFoundError") {
                errorMsg = "No camera found on this device.";
            } else if (err.name === "NotSecureError") {
                errorMsg = "Camera requires HTTPS connection.";
            } else if (err.name === "OverconstrainedError") {
                errorMsg = "Camera constraints not supported.";
            }
            setError(errorMsg);
            setCameraPermission("denied");
            setIsScanning(false);
        }
    };

    // Stop camera
    const stopCamera = () => {
        scanningRef.current = false;

        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach((track) => track.stop());
            videoRef.current.srcObject = null;
        }

        if (scanIntervalRef.current) {
            clearInterval(scanIntervalRef.current);
            scanIntervalRef.current = null;
        }
    };

    // Handle video play to start scanning
    const rotateImageData = (imageData, degrees) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = imageData.width;
        canvas.height = imageData.height;

        ctx.putImageData(imageData, 0, 0);

        const rotated = document.createElement("canvas");
        const rctx = rotated.getContext("2d");
        rotated.width = canvas.height;
        rotated.height = canvas.width;

        rctx.translate(rotated.width / 2, rotated.height / 2);
        rctx.rotate((degrees * Math.PI) / 180);
        rctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

        return rctx.getImageData(0, 0, rotated.width, rotated.height);
    };

    // Handle video play to start scanning
    const handleVideoPlay = () => {
        setDebugInfo("VIDEO PLAYING - Starting scan loop");
        if (scanIntervalRef.current) return;
        scanningRef.current = true;

        // Use interval instead of requestAnimationFrame
        const intervalId = setInterval(() => {
            // This runs every 50ms
            setTestCount((c) => c + 1);
            frameCounterRef.current += 1;

            if (
                !scanningRef.current ||
                !videoRef.current ||
                !canvasRef.current
            ) {
                return;
            }

            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            const video = videoRef.current;

            // Only scan if video has dimensions
            if (video.videoWidth && video.videoHeight) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx.drawImage(video, 0, 0);

                // Show dimensions every 50 frames
                if (frameCounterRef.current % 50 === 0) {
                    setDebugInfo(
                        `Canvas: ${canvas.width}x${canvas.height} | Frames: ${frameCounterRef.current}`
                    );
                }

                try {
                    const imageData = ctx.getImageData(
                        0,
                        0,
                        canvas.width,
                        canvas.height
                    );

                    // Try jsQR with different inversion attempts
                    const code = jsQR(
                        imageData.data,
                        imageData.width,
                        imageData.height,
                        {
                            inversionAttempts: "attemptBoth",
                        }
                    );

                    if (code && code.data) {
                        const now = Date.now();
                        if (
                            !lastDetectionRef.current ||
                            now - lastDetectionRef.current > 1500
                        ) {
                            lastDetectionRef.current = now;
                            setDebugInfo(`✅ DETECTED: ${code.data}`);
                            onQRCodeScanned(code.data);
                            return;
                        }
                    } else {
                        // QR not found in normal orientation, try rotated
                        const rotated = rotateImageData(imageData, 90);
                        const code2 = jsQR(
                            rotated.data,
                            rotated.width,
                            rotated.height,
                            { inversionAttempts: "attemptBoth" }
                        );

                        if (code2 && code2.data) {
                            const now = Date.now();
                            if (
                                !lastDetectionRef.current ||
                                now - lastDetectionRef.current > 1500
                            ) {
                                lastDetectionRef.current = now;
                                setDebugInfo(
                                    `✅ DETECTED (rotated): ${code2.data}`
                                );
                                onQRCodeScanned(code2.data);
                                return;
                            }
                        }
                    }
                } catch (e) {
                    setDebugInfo(`Error: ${e.message}`);
                }
            } else if (frameCounterRef.current % 20 === 0) {
                setDebugInfo("Waiting for camera focus/size…");
            }
        }, 50); // 20 FPS

        scanIntervalRef.current = intervalId;
    };

    // Handle camera startup/shutdown
    useEffect(() => {
        if (isScanning) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [isScanning]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    const handleStartScanning = () => {
        setError(null);
        setDebugInfo("");
        setTestCount(0);
        frameCounterRef.current = 0;
        noDetectionRef.current = 0;
        lastDetectionRef.current = null;
        setIsScanning(true);
    };

    const handleStopScanning = () => {
        setIsScanning(false);
        stopCamera();
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 3,
                px: 2,
            }}
        >
            {error && <Alert severity="error">{error}</Alert>}

            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "500px",
                    aspectRatio: "1",
                    border: "2px solid #ccc",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#000",
                }}
            >
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    onPlay={handleVideoPlay}
                    onError={(e) => {
                        console.error("Video error:", e);
                        setError("Error loading video stream");
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: isScanning ? "block" : "none",
                    }}
                />
                <canvas ref={canvasRef} style={{ display: "none" }} />

                {isScanning && (
                    <>
                        {/* QR scanning frame overlay */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: "200px",
                                height: "200px",
                                border: "2px solid lime",
                                boxShadow:
                                    "inset 0 0 20px rgba(0, 255, 0, 0.3)",
                            }}
                        />
                    </>
                )}

                {!isScanning && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#000",
                            color: "#fff",
                        }}
                    >
                        <Typography variant="body2" textAlign="center">
                            Camera preview will appear here
                        </Typography>
                    </Box>
                )}
            </Box>

            <Stack direction="row" spacing={2}>
                {!isScanning ? (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleStartScanning}
                        disabled={disabled}
                    >
                        Start Scanning
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={handleStopScanning}
                    >
                        Stop Scanning
                    </Button>
                )}
            </Stack>

            <Typography variant="caption" color="text.secondary" align="center">
                {isScanning
                    ? "Point your camera at a QR code to scan it"
                    : "Click 'Start Scanning' to begin scanning QR codes"}
            </Typography>

            {isScanning && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: "#000",
                        color: "#0f0",
                        borderRadius: 1,
                        width: "100%",
                        maxWidth: "500px",
                        border: "1px solid #0f0",
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "14px",
                            mb: 1,
                        }}
                    >
                        🔍 SCANNING ACTIVE
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "12px",
                            display: "block",
                            mb: 1,
                        }}
                    >
                        Frames processed: {testCount}
                    </Typography>
                    {debugInfo && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontFamily: "monospace",
                                fontSize: "12px",
                                color: "#ffff00",
                                display: "block",
                            }}
                        >
                            {debugInfo}
                        </Typography>
                    )}
                    {!debugInfo && testCount === 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontFamily: "monospace",
                                fontSize: "12px",
                                color: "#ff6666",
                                display: "block",
                            }}
                        >
                            ⚠️ No frames detected - camera may not be working
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default QRCodeScanner;
